import { Card, Checkbox, Chip, ClickAwayListener, FormControlLabel, Grid } from '@material-ui/core';
import { Icons, Select, TextField, Tooltip, Typography } from '@nutrien/cxp-components';
import React, { useEffect, useState } from 'react';

import { DeficiencyListModified } from '../../models/models';
import useAssetDetails from '../../rxdb/assetDetails/useAssetDetails';
import { ComponentAreaInfo } from '../../rxdb/deficiencyList/queryBuilder';
import { RemedialAction } from '../../rxdb/remedialAction/queryBuilder';
import { ComponentInfo, ComponentInfoLabel } from '../../utilities/enums';
import { isNull } from '../../utilities/utilityFunctions';
import useStyles from '../Deficiencies/Deficiencies.styles';

interface Props {
  deficiencyPayloadObj: DeficiencyListModified;
  remedialActionListData: RemedialAction[];
  selectComponentLocations: () => void;
  validateInputs: () => void;
  findEnvironmentalCondition: () => void;
  handleKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  addFlag: boolean;
  isEditable: boolean;
}

const ComponentInfoPanelForElectrical: React.FC<Props> = ({
  deficiencyPayloadObj,
  remedialActionListData,
  selectComponentLocations,
  validateInputs,
  findEnvironmentalCondition,
  handleKeyDown,
  addFlag,
  isEditable,
}) => {
  const classes = useStyles();
  const { result: assetData } = useAssetDetails();

  const deficiencyPayload = deficiencyPayloadObj;
  const [componentArea, setComponentArea] = useState<ComponentAreaInfo[]>([]);
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    setComponentArea(deficiencyPayload[ComponentInfo.COMPONENT_AREA_INFO]);
    findEnvironmentalCondition();
  }, [deficiencyPayload[ComponentInfo.COMPONENT_AREA_INFO]]);

  useEffect(() => {
    setComponentArea(deficiencyPayload[ComponentInfo.COMPONENT_AREA_INFO]);
    if (addFlag) deficiencyPayload[ComponentInfo.DATE_OBSERVED] = new Date().toISOString();
    deficiencyPayload[ComponentInfo.ASSET_CATEGORY_COMPONENT_ID] = 1;
    deficiencyPayload[ComponentInfo.FAILURE_CAUSE_CATEGORY] = 3;
  }, [deficiencyPayload[ComponentInfo.COMPONENT_AREA_INFO]]);

  const getValues = (name: string, value: string) => {
    let data;
    switch (name) {
      case ComponentInfo.REMEDIAL_ACTION:
        data = remedialActionListData.filter(item => item.id === value)[0];
        deficiencyPayload[name] = data.name;
        deficiencyPayload[ComponentInfo.REMEDIAL_ACTION_ID] = Number(data.id);
        return;
      default:
        return;
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    deficiencyPayload.isChanged = true;
    const { name, value } = event.target;
    if (name === ComponentInfo.REMEDIAL_ACTION || name === ComponentInfo.CAUSE_OF_FAILURE) {
      getValues(name, value);
    } else {
      deficiencyPayload[
        name as
          | ComponentInfo.COMPONENT_TYPE
          | ComponentInfo.NAME
          | ComponentInfo.COMMENT_OBSERVATION
      ] = value;
    }
    validateInputs();
  };

  const handleChangeCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    deficiencyPayload.isChanged = true;
    deficiencyPayload[ComponentInfo.IS_COMPLETED_DURING_INSPECTION] = event.target.checked
      ? true
      : false;
    validateInputs();
  };

  const handleDeleteComponentLocation = (id: number) => {
    deficiencyPayload.isChanged = true;
    deficiencyPayload[ComponentInfo.COMPONENT_AREA_INFO] = isNull(
      deficiencyPayload,
      ComponentInfo.COMPONENT_AREA_INFO,
      [],
    ).filter((val: ComponentAreaInfo) => val.areaId !== id);
    setComponentArea(deficiencyPayload[ComponentInfo.COMPONENT_AREA_INFO]);
    validateInputs();
  };

  const displayComponentLocation = (val: string) => {
    findEnvironmentalCondition();
    return val;
  };
  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };
  const chip = (val: ComponentAreaInfo) => {
    return (
      <Chip
        key={val.areaId}
        variant="outlined"
        clickable
        onDelete={() => handleDeleteComponentLocation(val.areaId)}
        label={displayComponentLocation(val.areaPcsId)}
        disabled={!isEditable}
        deleteIcon={!isEditable ? <></> : <Icons.MinusCircleFeather color="error" />}
      />
    );
  };

  return (
    <>
      <Card>
        <Grid className={classes.checkboxStyle} xs={12} item container>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                defaultChecked={deficiencyPayload[ComponentInfo.IS_COMPLETED_DURING_INSPECTION]}
                onChange={handleChangeCheckbox}
                disabled={!isEditable}
              />
            }
            label="Completed as part of Preventative Maintenance"
          />
        </Grid>
      </Card>
      <Grid className={classes.outerGrid} xs={12} item container>
        <Grid container spacing={2}>
          <Grid item container xs={12} alignItems="center">
            <Grid item xs={12} className={classes.compoInfoGrid}>
              <Typography variant="h6">{ComponentInfoLabel.DEFICIENCY_INFO}</Typography>
              <Typography variant="caption">
                <span>
                  Fields with<span className={classes.requierd}> *</span> are mandatory
                </span>
              </Typography>
              <hr className={`${classes.breakLine} ${classes.droppleContainer}`} />
            </Grid>

            <Grid item xs={12} className={classes.componentInfoDisplay}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" className="fieldLabel">
                  <span>
                    {ComponentInfoLabel.DEFICIENCY_NAME}
                    <span className={classes.requierd}> *</span>
                  </span>
                </Typography>
                <TextField
                  noMinHeight
                  name="name"
                  size="small"
                  placeholder="Type deficiency name"
                  onChange={handleChange}
                  defaultValue={deficiencyPayload.name ? deficiencyPayload.name : ''}
                  disabled={!isEditable}
                  onKeyDown={handleKeyDown}
                />
              </Grid>
              <Grid item xs={1} />
              <Grid item xs={6}>
                <Typography variant="subtitle2" className="fieldLabel">
                  <span>
                    {ComponentInfoLabel.COMP_LOCATION}
                    <span className={classes.requierd}> *</span>
                  </span>
                </Typography>
                <Grid item className={classes.componentLocationGrid}>
                  {componentArea?.length > 1 ? (
                    <ClickAwayListener onClickAway={handleTooltipClose}>
                      <Grid className={classes.displayToolTip}>
                        {componentArea?.map((val: ComponentAreaInfo, index: number) => {
                          if (index < 1) {
                            return chip(val);
                          }
                        })}
                        <Tooltip
                          PopperProps={{
                            disablePortal: true,
                          }}
                          onClose={handleTooltipClose}
                          open={open}
                          disableFocusListener
                          disableHoverListener
                          disableTouchListener
                          title={
                            <Grid className="tooltipLocation">
                              {componentArea?.map((val: ComponentAreaInfo, index: number) => {
                                if (index > 0) {
                                  return chip(val);
                                }
                              })}
                            </Grid>
                          }
                          placement="bottom"
                          arrow
                          className={classes.tooltip}
                        >
                          <Typography
                            color="primary"
                            className={classes.seeMoreText}
                            onClick={handleTooltipOpen}
                          >
                            {`See More (${componentArea?.length - 1})`}
                          </Typography>
                        </Tooltip>
                      </Grid>
                    </ClickAwayListener>
                  ) : (
                    <Grid>
                      {componentArea?.map((val: ComponentAreaInfo) => {
                        return chip(val);
                      })}
                    </Grid>
                  )}
                  {isEditable && (
                    <Icons.PlusCircleFeather
                      data-testid="clPlusButton"
                      color="primary"
                      className={classes.componentLocationPlusButton}
                      onClick={selectComponentLocations}
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.componentInfoDisplay}>
            <Grid item xs={6}>
              <Typography variant="subtitle2" className="fieldLabel">
                <span>
                  {ComponentInfoLabel.REMEDIAL_ACTION}
                  <span className={classes.requierd}> *</span>
                </span>
              </Typography>
              <Select
                name={ComponentInfo.REMEDIAL_ACTION}
                list={remedialActionListData}
                required
                getOptionLabel={list => `${list?.name}`}
                getOptionValue={list => list?.id}
                onChange={handleChange}
                placeholder="Select a action"
                defaultValue={isNull(deficiencyPayload, ComponentInfo.REMEDIAL_ACTION_ID, '')}
                className={`${classes.firstAutoComplete} ${classes.selectHeight}`}
                noMinHeight
                disabled={!isEditable}
              />
            </Grid>
            <Grid item xs={1} />
            <Grid item xs={6}>
              <Typography variant="subtitle2" className="fieldLabel">
                <span>{ComponentInfoLabel.EQUIP_CIRCUIT}</span>
              </Typography>
              <TextField
                noMinHeight
                name="name"
                size="small"
                placeholder="Type Equip/Circuit #"
                value={assetData ? assetData?.assetName || '-' : '-'}
                disabled
              />
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.componentInfoDisplay}>
            <Grid item xs={6}>
              <Typography variant="subtitle2" className="fieldLabel">
                <span>{ComponentInfoLabel.PRIMARY_CAUSE}</span>
              </Typography>
              <TextField
                name="commentAssetComponent"
                noMinHeight
                size="small"
                placeholder="Type Primary Cause of failure"
                onChange={handleChange}
                defaultValue={
                  deficiencyPayload.commentAssetComponent
                    ? deficiencyPayload.commentAssetComponent
                    : ''
                }
                disabled={!isEditable}
                onKeyDown={handleKeyDown}
              />
            </Grid>
            <Grid item xs={1} />
            <Grid item xs={6}>
              <Typography variant="subtitle2" className="fieldLabel">
                <span>
                  {ComponentInfoLabel.COMP_TYPE}
                  <span className={classes.requierd}> *</span>
                </span>
              </Typography>
              <TextField
                name="componentType"
                noMinHeight
                size="small"
                placeholder="Type component details"
                onChange={handleChange}
                defaultValue={
                  deficiencyPayload.componentType ? deficiencyPayload.componentType : ''
                }
                disabled={!isEditable}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.componentInfoDisplay}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" className="fieldLabel">
                <span>{ComponentInfoLabel.DEFICIENCY_DETAILS}</span>
              </Typography>
              <TextField
                name="commentObservation"
                placeholder="Input text"
                onChange={handleChange}
                defaultValue={
                  deficiencyPayload.commentObservation ? deficiencyPayload.commentObservation : ''
                }
                multiline
                variant="outlined"
                rows={3}
                rowsMax={3}
                disabled={!isEditable}
                noMinHeight
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default ComponentInfoPanelForElectrical;
